import type React from 'react';

import Color from 'color';

import { Text } from '@change/ds-core-ui';

import { usePetitionDetailsPageData, usePetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';
import { usePhoto } from '#src/app/pages/petitionDetails/shared/hooks';
import {
	headerDesktopTitleFadeGradient,
	headerTitleAreaBackground,
} from '#src/app/pages/petitionDetails/shared/utilities';

import { HeadingMedia } from '../HeadingMedia';
import { PetitionStarterInHeader } from '../PetitionStarterInHeader';

const defaultGradient =
	'linear-gradient(to left, rgba(128,128,128,1) 0%, rgba(128,128,128,1) 79%, rgba(128,128,128,0.3) 95%, rgba(128,128,128,0) 100%)';

function generateGradient(rgb: { r: number; g: number; b: number }): string {
	try {
		const baseColor = Color(rgb);
		const color1 = baseColor.rgb().string();
		const color2 = baseColor.alpha(0.3).rgb().string();
		const color3 = baseColor.alpha(0).rgb().string();

		return `linear-gradient(to left, ${color1} 0%, ${color1} 79%, ${color2} 95%, ${color3} 100%)`;
	} catch {
		return defaultGradient;
	}
}

export const HeadingWithPhoto: React.FC = () => {
	const {
		data: { petition },
	} = usePetitionDetailsPageData();
	const {
		data: {
			experiments: { petitionPageStarterInHeaderExperimentEnabled },
		},
	} = usePetitionDetailsPageUserData();
	const { user, organization, createdAt, createdAtLocalized } = petition;
	const photo = usePhoto();
	const title: string = petition.displayTitle;
	const averageColor = photo?.palette?.averageColor;
	const gradientValue = averageColor ? generateGradient(averageColor) : defaultGradient;
	const titleFadeGradient = headerDesktopTitleFadeGradient(averageColor);
	return (
		<div
			data-experiment="petition-header-experiment"
			className="max-h-[762px] w-full space-y-2 overflow-hidden rounded-xl md:aspect-2/1 [@media(max-height:500px)_and_(min-width:1351px)]:aspect-15/5!"
			style={{ backgroundColor: headerTitleAreaBackground(averageColor) }}
		>
			<div className="relative h-full w-full">
				<div className="mb-24 w-full">
					<div className="h-auto w-[75%] xl:w-[1015px]">
						{!!photo && <HeadingMedia key={petition.id} photo={photo} />}
					</div>
					<div
						className="absolute top-0 right-0 bottom-0 z-10 h-full w-[36%]"
						style={{ backgroundImage: gradientValue }}
					></div>
					<div
						className="absolute right-0 bottom-0 left-0 z-11 flex h-[76%] w-full flex-col justify-end pb-10 pl-10 bg-blend-multiply"
						style={{ backgroundImage: titleFadeGradient }}
					>
						<div className="space-y-2">
							<div className="mb-2.5 h-[12px] w-[71.2px] bg-[#E5231E]" />
							<Text
								data-qa="petition-title-desktop"
								variant="heading"
								as="h1"
								size="x-large"
								className="max-w-[64%] leading-tight font-bold text-wrap text-white lg:max-w-[71%]"
							>
								{title}
							</Text>
							<PetitionStarterInHeader
								user={user}
								organization={organization}
								createdAt={createdAt}
								createdAtLocalized={createdAtLocalized}
								petitionPageStarterInHeaderEnabled={petitionPageStarterInHeaderExperimentEnabled}
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
